import { useLocation, useNavigate } from 'react-router-dom'
import React, { useCallback, useMemo } from 'react'

import { useAdminAccess } from '../../providers/AdminAccessContext'

import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Badge,
  Box,
  Typography,
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import HomeIcon from '@mui/icons-material/Home'
import RateReviewIcon from '@mui/icons-material/RateReview'
import NotificationsIcon from '@mui/icons-material/Notifications'
import AssignmentIcon from '@mui/icons-material/Assignment'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import TuneIcon from '@mui/icons-material/Tune'
import StorageIcon from '@mui/icons-material/Storage'
import InsightsIcon from '@mui/icons-material/Insights'
import BusinessIcon from '@mui/icons-material/Business'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import DomainAddIcon from '@mui/icons-material/DomainAdd'
import GroupIcon from '@mui/icons-material/Group'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import { NotificationContext } from '../../providers/NotificationContext'
import HospitalIcon from '../../icons/HospitalIcon'
import AgencyIcon from '../../icons/AgencyIcon'

interface AppNavigationProps {
  open: boolean
  onNavigateComplete?: () => void
}

interface NavItemProps {
  icon: React.ReactNode
  text: string
  path: string
  selected: boolean
  open: boolean
  onNavigate: (path: string) => void
}

interface NavItemWithBadgeProps extends NavItemProps {
  badgeCount?: number
}

const NavItem = ({ icon, text, path, selected, open, onNavigate, badgeCount }: NavItemWithBadgeProps) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!selected) {
        onNavigate(path)
      }
    },
    [path, selected, onNavigate],
  )

  const content = (
    <ListItemButton
      onClick={handleClick}
      selected={selected}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        mx: 1,
        minHeight: 44,
        justifyContent: open ? 'flex-start' : 'center',
        px: open ? 2 : 1.5,
        color: '#263238',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#E0F7FA',
          color: '#007C91',
          '& .MuiListItemIcon-root': {
            color: '#007C91',
          },
        },
        '&.Mui-selected': {
          backgroundColor: '#007C91',
          color: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0, 124, 145, 0.2)',
          '&:hover': {
            backgroundColor: '#006B7D',
          },
          '& .MuiListItemIcon-root': {
            color: '#FFFFFF',
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: open ? 36 : 'auto',
          color: selected ? '#FFFFFF' : '#007C91',
          justifyContent: 'center',
        }}
      >
        {badgeCount !== undefined && badgeCount > 0 ? (
          <Badge badgeContent={badgeCount} color="error" max={99}>
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </ListItemIcon>
      {open && (
        <ListItemText
          primary={text}
          sx={{
            '& .MuiTypography-root': {
              fontWeight: selected ? 600 : 500,
              fontSize: '0.88rem',
            },
          }}
        />
      )}
    </ListItemButton>
  )

  if (!open) {
    return (
      <Tooltip title={text} placement="right" arrow>
        {content}
      </Tooltip>
    )
  }

  return content
}

/** Section header shown when drawer is expanded, thin divider when collapsed */
const SectionHeader = ({
  open,
  label,
  icon,
}: {
  open: boolean
  label: string
  icon?: React.ReactNode
}) => {
  if (!open) {
    return <Divider sx={{ my: 1.5, mx: 2, borderColor: 'rgba(0, 124, 145, 0.1)' }} />
  }

  return (
    <Box
      sx={{
        px: 2.5,
        pt: 2,
        pb: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      {icon && (
        <Box sx={{ display: 'flex', color: '#90A4AE', fontSize: 16 }}>{icon}</Box>
      )}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: '#90A4AE',
          textTransform: 'uppercase',
          letterSpacing: 1.2,
          fontSize: '0.65rem',
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

const AppNavigation = ({ open, onNavigateComplete }: AppNavigationProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { unreadCount } = React.useContext(NotificationContext)
  const { isAdmin } = useAdminAccess()

  const handleNavigate = useCallback(
    (path: string) => {
      // Compare full URL (pathname + search) to support query-param-only changes
      const currentFull = location.pathname + location.search
      // Normalize: path without query is just pathname, path with query includes ?...
      if (currentFull !== path && location.pathname + location.search !== path) {
        navigate(path)
      }
      // Close mobile drawer after navigation
      onNavigateComplete?.()
    },
    [navigate, location.pathname, location.search, onNavigateComplete],
  )

  // Check if a path matches current location (supports query params like ?type=agency)
  const isSelected = useCallback(
    (path: string, exact = false) => {
      if (exact) return location.pathname === path
      // For paths with query params, check pathname + search
      if (path.includes('?')) {
        const [pathname, search] = path.split('?')
        return location.pathname === pathname && location.search.includes(search)
      }
      return location.pathname.startsWith(path)
    },
    [location.pathname, location.search],
  )

  // ── General items (always visible) ──
  const generalItems = useMemo(
    () => [
      {
        icon: <HomeIcon />,
        text: 'Home',
        path: '/',
        selected: isSelected('/', true),
      },
      {
        icon: <GroupIcon />,
        text: 'Team',
        path: '/admin/team',
        selected: isSelected('/admin/team'),
      },
      {
        icon: <NotificationsIcon />,
        text: 'Notifications',
        path: '/notifications',
        selected: isSelected('/notifications'),
        badgeCount: unreadCount,
      },
    ],
    [isSelected, unreadCount],
  )

  // ── Data Management (admin only) ──
  const dataItems = useMemo(
    () => [
      {
        icon: <HospitalIcon />,
        text: 'Hospitals',
        path: '/hospitals',
        selected: isSelected('/hospitals'),
      },
      {
        icon: <AgencyIcon />,
        text: 'Agencies',
        path: '/agencies',
        selected: isSelected('/agencies'),
      },
    ],
    [isSelected],
  )

  // ── Reviews & Claims (admin only) ──
  const reviewClaimItems = useMemo(
    () => [
      {
        icon: <RateReviewIcon />,
        text: 'Reported Reviews',
        path: '/admin/reviews',
        selected: isSelected('/admin/reviews'),
      },
      {
        icon: <FactCheckIcon />,
        text: 'Hospital Claim Requests',
        path: '/admin/claim-pages',
        selected:
          isSelected('/admin/claim-pages') && !location.search.includes('type=agency'),
      },
      {
        icon: <VerifiedUserIcon />,
        text: 'Agency Claim Requests',
        path: '/admin/claim-pages?type=agency',
        selected:
          location.pathname.startsWith('/admin/claim-pages') &&
          location.search.includes('type=agency'),
      },
      {
        icon: <TuneIcon />,
        text: 'Customizations',
        path: '/admin/page-customizations',
        selected: isSelected('/admin/page-customizations'),
      },
    ],
    [isSelected, location.pathname, location.search],
  )

  // ── New Requests (admin only) ──
  const requestItems = useMemo(
    () => [
      {
        icon: <DomainAddIcon />,
        text: 'Hospital Requests',
        path: '/admin/hospital-requests',
        selected: isSelected('/admin/hospital-requests'),
      },
      {
        icon: <GroupAddIcon />,
        text: 'Agency Requests',
        path: '/admin/agency-requests',
        selected: isSelected('/admin/agency-requests'),
      },
    ],
    [isSelected],
  )

  return (
    <List
      component="nav"
      sx={{
        px: 0,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* ══════════════════════════════════════════════ */}
      {/* ── Admin Navigation ──                        */}
      {/* ══════════════════════════════════════════════ */}
      {isAdmin && (
        <>
          {/* General */}
          {generalItems.map((item) => (
            <ListItem disablePadding key={item.path}>
              <NavItem
                icon={item.icon}
                text={item.text}
                path={item.path}
                selected={item.selected}
                open={open}
                onNavigate={handleNavigate}
                badgeCount={'badgeCount' in item ? item.badgeCount : undefined}
              />
            </ListItem>
          ))}

          {/* Data Management */}
          <SectionHeader
            open={open}
            label="Data"
            icon={<StorageIcon sx={{ fontSize: 15 }} />}
          />
          {dataItems.map((item) => (
            <ListItem disablePadding key={item.path}>
              <NavItem
                icon={item.icon}
                text={item.text}
                path={item.path}
                selected={item.selected}
                open={open}
                onNavigate={handleNavigate}
              />
            </ListItem>
          ))}

          {/* Reviews & Claims */}
          <SectionHeader
            open={open}
            label="Reviews & Claims"
            icon={<InsightsIcon sx={{ fontSize: 15 }} />}
          />
          {reviewClaimItems.map((item) => (
            <ListItem disablePadding key={item.path}>
              <NavItem
                icon={item.icon}
                text={item.text}
                path={item.path}
                selected={item.selected}
                open={open}
                onNavigate={handleNavigate}
              />
            </ListItem>
          ))}

          {/* New Requests */}
          <SectionHeader
            open={open}
            label="Requests"
            icon={<NewReleasesIcon sx={{ fontSize: 15 }} />}
          />
          {requestItems.map((item) => (
            <ListItem disablePadding key={item.path}>
              <NavItem
                icon={item.icon}
                text={item.text}
                path={item.path}
                selected={item.selected}
                open={open}
                onNavigate={handleNavigate}
              />
            </ListItem>
          ))}
        </>
      )}

      {/* ── Spacer to push logout to bottom ── */}
      <Box sx={{ flexGrow: 1 }} />

      {/* ── Logout ── */}
      <Divider sx={{ my: 1, mx: 2, borderColor: 'rgba(0, 124, 145, 0.1)' }} />
      <ListItem disablePadding>
        <NavItem
          icon={<LogoutIcon />}
          text="Logout"
          path="/logout"
          selected={false}
          open={open}
          onNavigate={handleNavigate}
        />
      </ListItem>
    </List>
  )
}
export default AppNavigation
